import {
    InputGroup,
    InputGroupAddon,
    InputGroupInput,
    InputGroupText
} from "@solomon-labs/design/components/ui";
import React from "react";

import {
    DISABLED_VALUE_CLASS,
    READ_ONLY_GROUP_CLASS
} from "@/pages/shared/lightbarField";

/**
 * A light-bar field with its label inside: the DS `InputGroup` with an
 * inline-start addon holding `InputGroupText`, the same recipe as the Rewards
 * Destinations fields. Used by `/settings` (Profile) and `/team/:uuid` (the
 * member page), so the two Account sections read as one construction.
 *
 * `actions` are icon actions at the field's inline end (the pencil edit, its
 * save and cancel), inside the rail so every field keeps the same width.
 */
export function LabeledInput({
    label,
    groupClassName,
    className,
    actions,
    ...inputProps
}: {
    label: string;
    groupClassName?: string;
    actions?: React.ReactNode;
} & React.ComponentProps<typeof InputGroupInput>): React.ReactElement {
    return (
        <InputGroup className={`w-full ${groupClassName ?? ""}`}>
            <InputGroupAddon align="inline-start">
                {/* One label width for the page, so every value starts on the
                    same line ("Confirm New" is the longest label). */}
                <InputGroupText className="w-24">{label}</InputGroupText>
            </InputGroupAddon>
            <InputGroupInput
                {...inputProps}
                className={`${DISABLED_VALUE_CLASS} ${className ?? ""}`}
            />
            {actions && (
                <InputGroupAddon align="inline-end" className="gap-1">
                    {actions}
                </InputGroupAddon>
            )}
        </InputGroup>
    );
}

/** A value the viewer can read and copy but not change here. */
export function ReadOnlyField({
    label,
    value,
    actions
}: {
    label: string;
    value: string;
    actions?: React.ReactNode;
}): React.ReactElement {
    return (
        <LabeledInput
            label={label}
            groupClassName={READ_ONLY_GROUP_CLASS}
            type="text"
            value={value}
            readOnly
            aria-label={label}
            actions={actions}
        />
    );
}
